import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Check, X, Loader2, BookOpen, User, Scale, ShieldCheck, Lightbulb } from 'lucide-react';
import {
  ThreadCandidate,
  ThreadCandidateBatch,
  ThreadCandidateType,
  summarizeThread,
  getThreadCandidates,
  confirmThreadCandidate,
  dismissThreadCandidate,
} from '../../services/participantThreads';

interface ThreadCandidatesReviewProps {
  sessionId: string;
  avatarId: string;
  participantId: string;
}

const TYPE_CONFIG: Record<ThreadCandidateType, { label: string; icon: React.ReactNode; color: string }> = {
  story: { label: 'User Story', icon: <BookOpen size={12} />, color: 'bg-blue-50 text-blue-700 border-blue-100' },
  persona: { label: 'Persona', icon: <User size={12} />, color: 'bg-violet-50 text-violet-700 border-violet-100' },
  nfr: { label: 'NFR', icon: <ShieldCheck size={12} />, color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  adr: { label: 'ADR', icon: <Scale size={12} />, color: 'bg-amber-50 text-amber-700 border-amber-100' },
  insight: { label: 'Insight', icon: <Lightbulb size={12} />, color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

function renderCandidateContent(candidate: ThreadCandidate): React.ReactNode {
  const c = candidate.content || {};
  switch (candidate.type) {
    case 'story':
      return (
        <>
          <p className="text-sm text-slate-800">{c.story}</p>
          {c.persona && <p className="text-xs text-slate-400 mt-1">For: {c.persona}</p>}
          {Array.isArray(c.acceptanceCriteria) && c.acceptanceCriteria.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {c.acceptanceCriteria.map((ac: string, i: number) => (
                <li key={i} className="text-xs text-slate-500 pl-3 relative before:content-['\\2022'] before:absolute before:left-0">{ac}</li>
              ))}
            </ul>
          )}
        </>
      );
    case 'persona':
      return (
        <>
          <p className="text-sm font-semibold text-slate-800">{c.name} <span className="font-normal text-slate-400">— {c.role}</span></p>
          {c.context && <p className="text-xs text-slate-500 mt-1">{c.context}</p>}
        </>
      );
    case 'nfr':
      return (
        <>
          <p className="text-sm font-semibold text-slate-800">{c.title}</p>
          <p className="text-xs text-slate-500 mt-1">{c.description}</p>
        </>
      );
    case 'adr':
      return (
        <>
          <p className="text-sm font-semibold text-slate-800">{c.title}</p>
          <p className="text-xs text-slate-500 mt-1">{c.decision}</p>
        </>
      );
    case 'insight':
    default:
      return <p className="text-sm text-slate-800">{c.text}</p>;
  }
}

export const ThreadCandidatesReview: React.FC<ThreadCandidatesReviewProps> = ({ sessionId, avatarId, participantId }) => {
  const [batch, setBatch] = useState<ThreadCandidateBatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [summarizing, setSummarizing] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getThreadCandidates(sessionId, avatarId, participantId)
      .then(setBatch)
      .catch(() => setBatch(null))
      .finally(() => setLoading(false));
  }, [sessionId, avatarId, participantId]);

  useEffect(() => { load(); }, [load]);

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      await summarizeThread(sessionId, avatarId, participantId);
      // Candidate generation is async (n8n fires, then calls back) — poll briefly.
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        const result = await getThreadCandidates(sessionId, avatarId, participantId);
        if (result || attempts >= 10) {
          clearInterval(poll);
          setBatch(result);
          setSummarizing(false);
        }
      }, 3000);
    } catch {
      setSummarizing(false);
    }
  };

  const handleConfirm = async (candidateId: string) => {
    setResolvingId(candidateId);
    try {
      await confirmThreadCandidate(sessionId, avatarId, participantId, candidateId);
      load();
    } finally {
      setResolvingId(null);
    }
  };

  const handleDismiss = async (candidateId: string) => {
    setResolvingId(candidateId);
    try {
      await dismissThreadCandidate(sessionId, avatarId, participantId, candidateId);
      load();
    } finally {
      setResolvingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-400">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }

  const pendingCandidates = (batch?.candidates || []).filter(c => c.status === 'pending');

  return (
    <div className="border-t border-slate-200 bg-slate-50/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Candidates for this thread</h4>
        <button
          onClick={handleSummarize}
          disabled={summarizing}
          className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm disabled:opacity-60"
        >
          {summarizing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {summarizing ? 'Summarizing…' : batch ? 'Re-summarize' : 'Summarize'}
        </button>
      </div>

      {batch?.summary && (
        <p className="text-xs text-slate-500 italic mb-3">{batch.summary}</p>
      )}

      {!batch && !summarizing && (
        <p className="text-xs text-slate-400">No candidates yet — click Summarize to analyze this thread.</p>
      )}

      {batch && pendingCandidates.length === 0 && (
        <p className="text-xs text-slate-400">
          {batch.candidates.length === 0
            ? 'Nothing worth promoting was found in this thread.'
            : 'All candidates from this thread have been resolved.'}
        </p>
      )}

      <div className="space-y-2">
        {pendingCandidates.map(candidate => {
          const cfg = TYPE_CONFIG[candidate.type];
          const isResolving = resolvingId === candidate.id;
          return (
            <div key={candidate.id} className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cfg.color}`}>
                  {cfg.icon}
                  {cfg.label}
                </span>
                <span className="text-[11px] text-slate-400">{Math.round(candidate.confidence * 100)}% confidence</span>
              </div>
              {renderCandidateContent(candidate)}
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  onClick={() => handleConfirm(candidate.id)}
                  disabled={isResolving}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg border border-green-200 text-green-700 hover:bg-green-50 text-[11px] font-semibold transition-colors bg-white disabled:opacity-50"
                >
                  {isResolving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                  Confirm
                </button>
                <button
                  onClick={() => handleDismiss(candidate.id)}
                  disabled={isResolving}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-[11px] font-semibold transition-colors bg-white disabled:opacity-50"
                >
                  <X size={11} />
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ThreadCandidatesReview;
